import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { toast } from 'sonner'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SUPPORTED_LANGUAGES } from '@/shared/config/languages'
import { cn } from '@/shared/lib/utils'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { useUpdateLanguage } from '@/shared/hooks/api/user'

type AuthInterfaceLanguageSelectProps = {
  className?: string
}

export function AuthInterfaceLanguageSelect({
  className,
}: AuthInterfaceLanguageSelectProps) {
  const { t } = useTranslation('common')
  const { data: session, status: sessionStatus } = useGetUserSession()
  const { mutateAsync: updateLanguage, isPending: isUpdatingLanguage } =
    useUpdateLanguage()

  const supportedValues: string[] = SUPPORTED_LANGUAGES.map((lang) => lang.value)
  const rawLanguage = i18n.language
  const selectValue = supportedValues.includes(rawLanguage)
    ? rawLanguage
    : SUPPORTED_LANGUAGES[0].value

  const currentOption =
    SUPPORTED_LANGUAGES.find((lang) => lang.value === selectValue) ??
    SUPPORTED_LANGUAGES[0]

  const persistLanguageToProfile =
    sessionStatus === 'success' && session != null

  function handleLanguageChange(value: string) {
    void (async () => {
      try {
        if (persistLanguageToProfile) {
          await updateLanguage(value)
        }
        await i18n.changeLanguage(value)
      } catch {
        toast.error(t('settings.languageUpdateFailed'))
      }
    })()
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label className="sr-only" htmlFor="auth-interface-language">
        {t('settings.interfaceLanguageLabel')}
      </Label>
      <Select
        value={selectValue}
        onValueChange={(value) => {
          if (value) {
            handleLanguageChange(value)
          }
        }}
        disabled={persistLanguageToProfile && isUpdatingLanguage}
      >
        <SelectTrigger
          id="auth-interface-language"
          className="h-9 w-[min(100%,220px)]"
          aria-label={t('settings.interfaceLanguageLabel')}
        >
          <SelectValue>
            <span className="flex items-center gap-2">
              <span>{currentOption.flag}</span>
              <span>{currentOption.label}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <SelectItem key={lang.value} value={lang.value}>
              <span className="flex items-center gap-2">
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
